import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Button,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getDevices } from "../api/devices";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import DeviceCard from "../components/DeviceCard";

function DeviceListPage() {
  const { username } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch devices with useCallback to avoid recreating the function on each render
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDevices();
      setDevices(data);
      console.log("Fetched devices:", data);
    } catch (err) {
      console.error("Error loading devices:", err.message);
      setError("Failed to load devices");
    } finally {
      setLoading(false);
    }
  }, []);

  // Only run once on mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleDeviceSelect = (deviceSerial) => {
    navigate(`/device-controls/${deviceSerial}`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "online":
      case "active":
      case "charging":
        return "success";
      case "cleaning":
        return "primary";
      case "offline":
      case "error":
        return "error";
      case "stopped":
      case "paused":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusText = (device) => {
    // You can customize this based on your device data structure
    return device.status || device.state || "Unknown";
  };

  return (
    <>
      <TopNavBar
        title="Device Controls"
        username={username}
        heading="Device Controls"
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Available Devices
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a device to view its controls and status
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : devices.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No devices found
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {devices
              .filter((device) => device.active === 1) // Only show currently active devices
              .map((device, index) => (
                <Grid
                  key={device.device_id || device.pk_id || index}
                  size={{ xs: 12, sm: 6, md: 4 }}
                >
                  <DeviceCard
                    device={device}
                    size={"medium"}
                    urlPrefix={"/device-controls/"}
                    buttonText={"Controls"}
                    disabled={
                      device.manufacturer?.toLowerCase() !== "lionsbot"
                        ? true
                        : false
                    }
                  />
                </Grid>
              ))}
          </Grid>
        )}
      </Container>
    </>
  );
}

export default DeviceListPage;
